import { EventEmitter } from 'events';
import ElementRandomiser from './ElementRandomiser';
import {
    MouseClickTypes,
    PreloadPolicy,
    PreloadProgress,
    SessionState,
    TestElementType,
    TestState,
    MessageType
} from './Enums';
import { Reasons } from './Reasons/Reasons';
import LoadingMediaScreen from './Screens/LoadingMediaScreen';
import SessionService from './SessionService';
import InstructionPlayer from './Players/InstructionPlayer';
import ImagePlayer from './Players/ImagePlayer';
import VideoPlayer from './Players/VideoPlayer';
import TextPlayer from './Players/TextPlayer';
import Environment from './Environment';
import { hide, show, UiService } from './UiService';
import ConnectingMediaServerScreen from './Screens/ConnectingMediaServerScreen';
import TestScreen from './Screens/TestScreen';
import ProxyRecorder from './ProxyRecorder';
import ResultCollector from './ResultCollector';
import { getTimer } from './TimerUtils';
import RedirectService from './RedirectService';
import ErrorService from './ErrorService';
import MessageService from './MessageService';
import FaceService from './FaceService';
import URLPlayer from './Players/URLPlayer';

export default class Test extends EventEmitter {
    /**
     * Test constructor
     * @param {ElementRandomiser} elementRandomiser
     * @param {SessionService} sessionService
     * @param {Environment} environment
     * @param {UiService} uiService
     * @param {ProxyRecorder} recorder
     * @param {ResultCollector} resultCollector
     * @param {RedirectService} redirectService
     * @param {ErrorService} errorService
     * @param {MessageService} messageService
     * @param {FaceService} faceService
     * @param {any} options
     */
    constructor(
        elementRandomiser,
        sessionService,
        environment,
        uiService,
        recorder,
        resultCollector,
        redirectService,
        errorService,
        messageService,
        faceService,
        options
    ) {
        super();

        this.elementRandomiser = elementRandomiser;
        this.sessionService = sessionService;
        this.environment = environment;
        this.uiService = uiService;
        this.recorder = recorder;
        this.recorder.on('error', this.onRecorderError);
        this.resultCollector = resultCollector;
        this.redirectService = redirectService;
        this.errorService = errorService;
        this.messageService = messageService;
        this.faceService = faceService;

        this.autoplayNext = true;
        this.testElements = null;
        this.playerSequence = [];
        this.currentPlayerIndex = 0;
        this.needPreload = this._needPreload(options.preloadPolicy);
        this.isReviewer= options.isReviewer;
        this.isClosed = false;
        this.showCloseTimeout;
        this.oldX = 0;
        this.oldY = 0;
    }

    init(elements) {
        this.testElements = this.elementRandomiser.randomise(elements.elements);

        if (this.testElements && this.testElements.length > 0) {
            this.testElements.forEach((elem) => {
                switch (elem.testElementTypeID) {
                    case TestElementType.IMAGE:
                        const imagePlayer = new ImagePlayer(elem);
                        imagePlayer.preload(this.needPreload).then(() => this.preloadCompleted());

                        this.playerSequence.push(imagePlayer);
                        break;
                    case TestElementType.VIDEO:
                        const videoPlayer = new VideoPlayer(elem, this.environment, this.uiService);
                        videoPlayer.preload(this.needPreload).then(() => this.preloadCompleted());

                        this.playerSequence.push(videoPlayer);
                        break;
                    case TestElementType.INSTRUCTION:
                        this.playerSequence.push(new InstructionPlayer(elem));
                        break;
                    case TestElementType.URL:
                        this.playerSequence.push(new URLPlayer(elem, this.uiService));
                        break;
                    case TestElementType.TEXT:
                        this.playerSequence.push(new TextPlayer(elem, this.sessionService, this.uiService));
                        break;
                }
            });

            this.loadingProgress();
        }
    }

    _needPreload(preloadPolicy) {
        switch (preloadPolicy) {
            case PreloadPolicy.PRELOAD_ALL:
            case PreloadPolicy.PRELOAD_ONE:
                return true;
            default:
                return false;
        }
    }

    preload() {
        return new Promise(async (resolve, reject) => {
            try {
                console.log('Preload called');

                this.preloadResolve = resolve;

                this.broadcastPreloadProgress(PreloadProgress.started, 0);

                await this.sessionService.changeState(
                    SessionState.preloading_media,
                    Reasons.environment_checks_succeeded
                );
                this.preloadCompleted();
            } catch (e) {
                reject(e);
            }
        });
    }

    preloadCompleted() {
        if (typeof this.preloadResolve !== 'function') {
            return;
        }

        const arePlayersPreloaded = this.playerSequence.every((player) => player.isPreloaded);
        if (arePlayersPreloaded) {
            this.broadcastPreloadProgress(PreloadProgress.finished, 100);
            this.preloadResolve();
        }
    }

    startTest() {
        this.broadcastTestStateChanged(TestState.started);

        this.sessionService.changeState(SessionState.connecting_to_media_server, Reasons.media_preloaded);

        show(document.getElementById('close-button-holder'), true);

        const closeButton = document.getElementById('close');
        if (closeButton) {
            closeButton.addEventListener('click', (e) => this.onCloseTest(e));
            closeButton.addEventListener('hover', (e) => this.clearCloseTimeout(e));
        }

        this.setCloseTimeout();
        document.addEventListener('mousemove', (e) => this.onMouseMove(e));
    }

    loadingProgress(data) {
        if (data && data.remainingTimeEstimate) {
            document.getElementById('timer').innerText = getTimer(data.remainingTimeEstimate / 1000);
        }

        this.broadcastPreloadProgress(PreloadProgress.inProgress, data);
    }

    async initVideoPlayers() {
        if (this.environment.isIOS) {
            const videoPlayers = this.playerSequence.filter((p) => p instanceof VideoPlayer);
            await Promise.all(videoPlayers.map(async (vp) => await vp.initPlayer()));
        }
    }

    // Recording
    async startRecording() {
        new ConnectingMediaServerScreen(this.uiService).display();
        const recordingId = await this.recorder.start();

        if (
            this.environment.isMobile &&
            this.environment.isChrome &&
            this.recorder.config.recorderOptions.mirrorViewConfig
        ) {
            await this.recorder.showMirrowView();
        }

        this.sessionService.logMessage('info', 'Recording started with id: ' + recordingId);

        this.sessionService.changeState(SessionState.collecting, Reasons.connected_mediaserver, null, {
            recordingId: this.recorder.recordingId,
            startTime: this.recorder.connectedTime
        });

        this.resultCollector.start(this.playerSequence);
    }

    async stopRecording() {
        await this.recorder.stop();
        this.resultCollector.stopPlayerPolling();
    }

    // Play
    playAll() {
        return new Promise((resolve) => {
            let continuePlaying = true;

            // the timeout to eliminate OT sync gap at beggining of recording
            setTimeout(async () => {
                do {
                    if (this.autoplayNext || !this.isClosed) {
                        continuePlaying = await this.playNext();
                    } else {
                        break;
                    }
                } while (continuePlaying);

                const currentPlayer = this.playerSequence[--this.currentPlayerIndex];
                this.broadcastTestStateChanged(TestState.finished, currentPlayer.testElement);
                this.showTestScreen();
                if (document.fullscreenElement) {
                    document.exitFullscreen();
                }
                resolve();
            }, 1200);
        });
    }

    async playNext() {
        if (this.playerSequence.length > this.currentPlayerIndex) {
            this.currentPlayerIndex++;
            await this.playCurrentTestElement();
            return true; // true => there may be more elements
        }
        if ((window.jsModuleSettings || {}).stopRecordingAfterLastMedia === 'true') {
            this.stopRecording();
        }

        if (document.fullscreenElement) {
            document.exitFullscreen();
        }

        return false; // false => reached last element
    }

    async playCurrentTestElement() {
        const currentPlayer = this.playerSequence[this.currentPlayerIndex - 1];

        currentPlayer.isActive = true;

        if (currentPlayer.testElement.cutoffTime != null && currentPlayer.testElement.cutoffTime > 0) {
            this.showTestScreen();
        }

        if (currentPlayer.show) {
            currentPlayer.show();
        }

        this.broadcastTestStateChanged(TestState.playing, currentPlayer.testElement);

        this.showSkipContinue(currentPlayer);

        await currentPlayer.start();

        currentPlayer.isActive = false;

        this.broadcastTestStateChanged(TestState.finishPlaying, currentPlayer.testElement);

        this.hideSkipContinue(currentPlayer);

        if (currentPlayer.hide) {
            currentPlayer.hide();
        }

        if (this.playerSequence.length == this.currentPlayerIndex && document.fullscreenElement) {
            document.exitFullscreen();
        }
    }

    // End test
    async complete(enableRedirect = true) {
        hide(document.getElementById('close-button-holder'));

        this.messageService.displayMessage('saving', 'dc_please_wait');

        this.redirectService.enableRedirect = enableRedirect;

        await this.stopRecording();
        if (window.jsModuleSettings.isFaceUniquenessEnabled && !this.isReviewer) {
            await this.faceService.promoteToPermanent(
                window.jsModuleSettings.collectionGroupId,
                this.sessionService.sessionId.toString()
            );
        }

        if (this.isClosed) {
            this.errorService.aborted();
        } else {
            await this.resultCollector.savePoll(true);

            await this.sessionService.changeState(
                SessionState.collected,
                Reasons.success_study_completed_results_submitted
            );
        }
    }

    onRecorderError(msg) {
        this.errorService.error(Reasons.other_error, msg, true);

        this.emit('recorderError', msg);
    }

    showSkipContinue(currentPlayer) {
        const button = this.getSkipContinueButton(currentPlayer);

        if (button) {
            const buttonClickHandler = (e) => {
                e.preventDefault();
                currentPlayer.skip();
            };

            button.addEventListener('click', buttonClickHandler, {
                once: true
            });

            button.style.display = 'inline-block';
        }
    }

    hideSkipContinue(currentPlayer) {
        const button = this.getSkipContinueButton(currentPlayer);

        if (button) {
            button.style.display = 'none';
        }
    }

    getSkipContinueButton(currentPlayer) {
        const currentTestElement = currentPlayer.testElement;

        if (!currentTestElement || !currentTestElement.continueOnClick) {
            return;
        }
        const continueOnClick = currentTestElement.continueOnClick;

        switch (continueOnClick) {
            case MouseClickTypes.VISIBLE_SKIP_BUTTON:
                return document.getElementById('test-element-skip');
            case MouseClickTypes.VISIBLE_CONTINUE_BUTTON:
                return document.getElementById('test-element-continue');
            default:
                return;
        }
    }

    handleConnectedToMediaServer() {
        this.sessionService.changeState(SessionState.collecting, Reasons.connected_mediaserver, null, {
            recordingId: this.recorder.recordingId,
            startTime: this.recorder.connectedTime
        });

        this.showTestScreen();
    }

    showLoadingMediaScreen() {
        new LoadingMediaScreen(this.uiService).display();
    }

    showTestScreen() {
        new TestScreen(this.uiService).display();
    }

    async stopCurrentTestElement() {
        const currentPlayer = this.playerSequence[this.currentPlayerIndex - 1];

        currentPlayer.isActive = false;

        if (currentPlayer.hide) {
            currentPlayer.hide();
        }

        this.hideSkipContinue(currentPlayer);

        await currentPlayer.stop();

        return currentPlayer.testElement;
    }

    onCloseTest(e) {
        e.preventDefault();
        this.isClosed = true;

        this.playerSequence.forEach((p) => p.close());

        this.complete();
    }

    onMouseMove(e) {
        e.preventDefault();
        if (e.target.id !== 'close' && (this.oldX !== e.pageX || this.oldY !== e.pageY)) {
            this.clearCloseTimeout(e);
            show(document.getElementById('close'));
            this.setCloseTimeout();
            this.oldX = e.pageX;
            this.oldY = e.pageY;
        }
    }

    setCloseTimeout() {
        this.showCloseTimeout = setTimeout(function () {
            hide(document.getElementById('close'));
        }, 2000);
    }

    clearCloseTimeout(e) {
        e.preventDefault();
        if (this.showCloseTimeout) {
            clearTimeout(this.showCloseTimeout);
        }
    }

    broadcastPreloadProgress(preloadState, progress) {
        this.emit('preloadProgress', {
            state: preloadState,
            progress: progress
        });
    }

    broadcastTestStateChanged(testState, element = null) {
        const data = {
            state: testState
        };

        if (element) {
            data.element = element;
        }

        this.emit('testStateChanged', data);
    }
}
